import { useEffect, useRef, useState } from 'react';
import { GrainGradient } from '@paper-design/shaders-react';

const HOME_GRAIN_PALETTES = {
  light: {
    colorBack: '#ffffff',
    colors: ['#6f9cf6', '#82aef7', '#8587ec', '#a28ee0']
  },
  dark: {
    colorBack: '#111827',
    colors: ['#7300ff', '#eba8ff', '#00bfff', '#2b00ff']
  },
  'true-dark': {
    colorBack: '#000000',
    colors: ['#7300ff', '#eba8ff', '#00bfff', '#2b00ff']
  },
  neon: {
    colorBack: '#141b17',
    colors: ['#2f7df4', '#417ee8', '#625bd8', '#8a55c2']
  }
};

const WEBGL_CONTEXT_ATTRIBUTES = {
  alpha: false,
  antialias: false,
  powerPreference: 'low-power'
};

const HomeGrainGradient = ({
  theme = 'true-dark',
  className = '',
  maxPixelCount = 520000
}) => {
  const containerRef = useRef(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const palette = HOME_GRAIN_PALETTES[theme] || HOME_GRAIN_PALETTES['true-dark'];

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) return undefined;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let isVisible = false;

    const updateAnimation = () => {
      setShouldAnimate(
        isVisible
        && !reducedMotionQuery.matches
        && document.visibilityState === 'visible'
      );
    };

    const handleVisibilityChange = () => updateAnimation();
    const handleMotionChange = () => updateAnimation();
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(([entry]) => {
        isVisible = Boolean(entry?.isIntersecting);
        updateAnimation();
      }, { rootMargin: '18% 0px', threshold: 0.01 })
      : null;

    if (observer) {
      observer.observe(containerRef.current);
    } else {
      isVisible = true;
      updateAnimation();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', handleMotionChange);
    } else {
      reducedMotionQuery.addListener?.(handleMotionChange);
    }

    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (reducedMotionQuery.removeEventListener) {
        reducedMotionQuery.removeEventListener('change', handleMotionChange);
      } else {
        reducedMotionQuery.removeListener?.(handleMotionChange);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`home-grain-gradient ${className}`.trim()}
      data-animated={shouldAnimate ? 'true' : 'false'}
      aria-hidden="true"
    >
      <GrainGradient
        className="home-grain-gradient__shader"
        width="100%"
        height="100%"
        colors={palette.colors}
        colorBack={palette.colorBack}
        softness={0.5}
        intensity={0.5}
        noise={0.25}
        shape="corners"
        speed={shouldAnimate ? 1 : 0}
        frame={0}
        scale={1}
        rotation={0}
        offsetX={0}
        offsetY={0}
        fit="cover"
        minPixelRatio={1}
        maxPixelCount={maxPixelCount}
        webGlContextAttributes={WEBGL_CONTEXT_ATTRIBUTES}
      />
    </div>
  );
};

export default HomeGrainGradient;
